import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { MY_AFFILIATE_REFERRED_USER_EARNINGS } from '@/queries/queries';
import { formatCents } from '@/lib/format/currency';
import { deriveEarningDisplayStatus } from '@/lib/affiliates/deriveEarningDisplayStatus';
import type { AffiliateReferredUser, AffiliateReferredUserEarning } from '@/generated/graphql';

interface ReferredUsersCardProps {
  referredUsers: AffiliateReferredUser[];
  totalReferred: number;
}

export const ReferredUsersCard = ({ referredUsers, totalReferred }: ReferredUsersCardProps) => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<AffiliateReferredUser | null>(null);
  const [loadEarnings, { data, loading, error }] = useLazyQuery<{
    myAffiliateReferredUserEarnings: AffiliateReferredUserEarning[];
  }>(MY_AFFILIATE_REFERRED_USER_EARNINGS);

  const users = referredUsers ?? [];

  const sourceLabel: Record<string, string> = {
    payment: t('affiliates.earnings.sourcePayment'),
    course: t('affiliates.earnings.sourceCourse'),
    cpm: t('affiliates.earnings.sourceCpm'),
  };
  const statusLabel: Record<string, string> = {
    pending: t('affiliates.earnings.statusPending'),
    available: t('affiliates.earnings.statusAvailable'),
    reversed: t('affiliates.earnings.statusReversed'),
  };
  const statusVariant: Record<string, 'secondary' | 'default' | 'destructive'> = {
    pending: 'secondary',
    available: 'default',
    reversed: 'destructive',
  };
  const now = new Date();

  const openDetails = (user: AffiliateReferredUser) => {
    setSelected(user);
    loadEarnings({ variables: { userId: user.id } });
  };

  const details = data?.myAffiliateReferredUserEarnings ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('affiliates.referred.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('affiliates.referred.empty')}</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('affiliates.referred.name')}</TableHead>
                  <TableHead>{t('affiliates.referred.signedUp')}</TableHead>
                  <TableHead>{t('affiliates.referred.status')}</TableHead>
                  <TableHead>{t('affiliates.referred.earned')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow
                    key={u.id}
                    className="cursor-pointer"
                    onClick={() => openDetails(u)}
                    data-testid="affiliate-referred-row"
                  >
                    <TableCell>{u.name || '—'}</TableCell>
                    <TableCell>{new Date(u.createdAt).toLocaleDateString(i18n.language)}</TableCell>
                    <TableCell>
                      <Badge variant={u.has_paid ? 'default' : 'secondary'}>
                        {u.has_paid
                          ? t('affiliates.referred.paid')
                          : t('affiliates.referred.pendingPayment')}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCents(u.total_commission_cents, 'BRL')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalReferred > users.length && (
              <p className="text-sm text-muted-foreground mt-2">
                {t('affiliates.referred.showingRecent')}
              </p>
            )}
          </>
        )}

        <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {t('affiliates.referred.detailsTitle', { name: selected?.name || '—' })}
              </DialogTitle>
            </DialogHeader>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : error ? (
              <p className="text-sm text-destructive">{t('affiliates.error')}</p>
            ) : details.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('affiliates.referred.detailsEmpty')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('affiliates.earnings.date')}</TableHead>
                    <TableHead>{t('affiliates.earnings.source')}</TableHead>
                    <TableHead>{t('affiliates.earnings.paymentAmount')}</TableHead>
                    <TableHead>{t('affiliates.earnings.commission')}</TableHead>
                    <TableHead>{t('affiliates.earnings.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {details.map((e) => {
                    const display = deriveEarningDisplayStatus({
                      status: e.status,
                      paymentDate: e.date ? new Date(e.date) : null,
                      createdAt: new Date(e.date),
                      now,
                    });
                    return (
                      <TableRow key={e.id}>
                        <TableCell>{new Date(e.date).toLocaleDateString(i18n.language)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{sourceLabel[e.source] ?? e.source}</Badge>
                        </TableCell>
                        <TableCell>
                          {e.source === 'cpm' ? '—' : formatCents(e.payment_amount_cents, 'BRL')}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCents(e.commission_cents, 'BRL')}
                          <span className="text-muted-foreground text-xs ml-1">({e.commission_rate}%)</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant[display]}>{statusLabel[display]}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
